/*
 * Pure display quotes for a NEW purchase selection, never for an existing cart line.
 * The caller must supply a fresh, same-session Ajax cart and refresh on cart updates,
 * pageshow and return to the page. Do not persist a successful code as eligibility.
 * Unknown discounts deliberately suppress speculative prices, including a bundle
 * price that could lose to a non-combinable discount in the actual cart.
 * Single-size chooser prices never inherit a quantity discount from the cart;
 * Shopify remains responsible for the discounts applied to actual cart items.
 */
function cistaPurchaseQuotes(config, cart) {
  const small = config?.prices?.small;
  const large = config?.prices?.large;
  if (![small, large].every(value => Number.isSafeInteger(value) && value >= 0)
    || !Number.isSafeInteger(small + large)) {
    throw new TypeError('CISTA prices must be non-negative integer currency subunits');
  }

  const list = value => Array.isArray(value) ? value : [];
  const positive = value => Number.isFinite(Number(value)) && Number(value) > 0;
  const name = value => typeof value === 'string' ? value.trim().toUpperCase() : '';
  const id = value => value == null ? '' : String(value).split('/').pop();
  const reduction = (amount, percent) => amount - Math.floor(amount * percent / 100);
  const quote = (amount, original, productPercent, orderPercent, orderCode) => ({
    amount,
    original,
    discounted: amount < original,
    productPercent,
    orderPercent,
    orderCode: orderPercent > 0 ? orderCode : '',
    // Diagnostic arithmetic only. UI must show product and order discounts
    // separately and must never render this combined percentage.
    percent: amount < original && original > 0 ? Math.round((original - amount) * 100 / original) : 0
  });
  const result = (orderPercent, setProductPercent = 20, orderCode = '') => {
    const price = (amount, productPercent) => reduction(reduction(amount, productPercent), orderPercent);
    return {
      small: quote(reduction(small, orderPercent), small, 0, orderPercent, orderCode),
      large: quote(reduction(large, orderPercent), large, 0, orderPercent, orderCode),
      set: quote(price(small, setProductPercent) + price(large, setProductPercent), small + large, setProductPercent, orderPercent, orderCode)
    };
  };
  const defaults = () => result(0);
  const regularCista = item => {
    const variant = id(item?.variant_id ?? item?.id);
    const sameVariant = variant && [id(config.smallId), id(config.largeId)].includes(variant);
    const sameProduct = !item?.product_id || !config.productId || id(item.product_id) === id(config.productId);
    return sameVariant && sameProduct && !item.selling_plan_allocation && !item.selling_plan && !item.gift_card;
  };

  if (!cart || !Array.isArray(cart.items) || !name(config.currency)
    || name(cart.currency) !== name(config.currency)) return defaults();

  const orderRule = config.orderDiscount;
  const orderPercent = Number(orderRule?.percent);
  const ruleValid = name(orderRule?.code) && Number.isFinite(orderPercent) && orderPercent > 0 && orderPercent <= 100;
  const sameOrderCode = application => ruleValid && name(application?.title) === name(orderRule.code);
  const verifiedOrderApplication = application => sameOrderCode(application)
    && application.type === 'discount_code'
    && application.value_type === 'percentage'
    && Number(application.value) === orderPercent
    && application.target_type === 'line_item'
    && application.target_selection === 'all';
  // This title/rate/context was checked against the native Shopify discount.
  // An app allocation named Kaching is not considered equivalent evidence.
  const nativeTitle = 'Buy 2 or more – 20% off';
  const nativeProductApplication = (application, item, allocationAmount) => {
    if (!regularCista(item) || name(application?.title) !== name(nativeTitle)
      || application.value_type !== 'percentage' || Number(application.value) !== 20
      || application.target_type !== 'line_item'
      || !['all', 'entitled', 'explicit'].includes(application.target_selection)) return false;
    if (application.type === 'automatic') return true;
    // Verified GET /cart.js sometimes returns type:'' for this native allocation;
    // update.js identifies it as automatic. Do not generalize an empty type to
    // any discount: require the exact observed shape and unit-price arithmetic.
    const original = Number(item.original_price);
    const quantity = item.quantity;
    return application.type === '' && application.allocation_method === 'each'
      && application.target_selection === 'entitled'
      && Number.isSafeInteger(original) && original >= 0
      && Number.isSafeInteger(quantity) && quantity > 0
      && Number(allocationAmount) === Math.floor(original * 20 / 100) * quantity
      && Number(item.final_price) === reduction(original, 20)
      && Number(item.final_line_price) === reduction(original, 20) * quantity;
  };

  const orderApplications = list(cart.cart_level_discount_applications)
    .map(entry => {
      if (!entry?.discount_application) return entry;
      // update.js can wrap an application in an allocation; GET uses the flat
      // application. An explicit zero wrapper amount is not positive evidence.
      const amount = entry.amount == null ? entry.discount_application.total_allocated_amount : entry.amount;
      return { ...entry.discount_application, total_allocated_amount: amount };
    })
    .filter(application => positive(application?.total_allocated_amount));
  const knownOrder = orderApplications.filter(verifiedOrderApplication);
  let unknown = orderApplications.some(application => !verifiedOrderApplication(application)) || knownOrder.length > 1;
  let allocated = orderApplications.reduce((sum, application) => sum + Number(application.total_allocated_amount), 0);
  let nativeFound = false;
  let eligibleQuantity = 0;

  for (const item of cart.items) {
    if (regularCista(item) && Number.isSafeInteger(item.quantity) && item.quantity > 0) eligibleQuantity += item.quantity;
    const allocations = list(item.line_level_discount_allocations).filter(allocation => positive(allocation?.amount));
    let itemNative = false;
    for (const allocation of allocations) {
      allocated += Number(allocation.amount);
      if (nativeProductApplication(allocation.discount_application, item, allocation.amount)) {
        if (itemNative) unknown = true;
        itemNative = nativeFound = true;
      }
      else unknown = true;
    }
    // A price decrease with no explainable allocation is not safe to extrapolate.
    if (Number.isFinite(Number(item.original_price)) && Number.isFinite(Number(item.final_price))
      && Number(item.final_price) < Number(item.original_price) && !itemNative) unknown = true;
    for (const discount of list(item.discounts)) {
      if (!positive(discount?.amount)) continue;
      const isNative = itemNative && name(discount.title) === name(nativeTitle);
      const isOrder = knownOrder.length === 1 && sameOrderCode(discount);
      if (!isNative && !isOrder) unknown = true;
    }
  }

  // Don't silently overlook a legacy or app discount missing rich metadata.
  if (positive(cart.total_discount) && Number(cart.total_discount) > allocated) unknown = true;
  if (nativeFound && eligibleQuantity < 2) unknown = true;
  const codes = list(cart.discount_codes);
  if (codes.some(code => code?.applicable === true && name(code.code) !== name(orderRule?.code))) unknown = true;
  const rejectedOrder = codes.some(code => name(code?.code) === name(orderRule?.code) && code.applicable === false);
  if (rejectedOrder && knownOrder.length) unknown = true;
  if (unknown) return result(0, 0);

  const confirmedOrder = knownOrder.length === 1 && !rejectedOrder;
  // Without the audited combination rule, use the regular known offer; don't
  // invent a combined price from a successful code on a different cart state.
  if (confirmedOrder && orderRule.combinesWithBundle !== true) return result(0, 0);
  return result(confirmedOrder ? orderPercent : 0, 20, confirmedOrder ? knownOrder[0].title.trim() : '');
}

if (!customElements.get('cista-purchase')) {
  customElements.define('cista-purchase', class CistaPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.mountSticky();
        this.observeSticky();
        this.observePrices();
        return;
      }
      this.form = this.querySelector('form');
      this.fieldset = this.querySelector('[data-purchase-choices]');
      this.radios = Array.from(this.querySelectorAll('.cista-purchase__radio'));
      this.variantInput = this.querySelector('[data-purchase-variant]');
      this.button = this.querySelector('[data-purchase-submit]');
      this.buttonText = this.querySelector('[data-purchase-button-text]');
      this.buttonPrice = this.querySelector('[data-purchase-button-price]');
      this.spinner = this.querySelector('[data-purchase-spinner]');
      this.error = this.querySelector('[data-purchase-error]');
      this.errorText = this.querySelector('[data-purchase-error-text]');
      this.cartLink = this.querySelector('[data-purchase-cart-link]');
      this.sticky = this.querySelector('[data-purchase-sticky]');
      this.stickyButton = this.querySelector('[data-purchase-sticky-button]');
      this.stickyChoice = this.querySelector('[data-purchase-sticky-choice]');
      this.stickyPrice = this.querySelector('[data-purchase-sticky-price]');
      this.stickyMedia = this.querySelector('[data-purchase-sticky-media]');
      this.stickyCompare = this.querySelector('[data-purchase-sticky-compare]');
      this.stickyOriginal = this.querySelector('[data-purchase-sticky-original]');
      this.orderDiscount = this.querySelector('[data-purchase-order-discount]');
      try {
        this.config = JSON.parse(this.querySelector('[data-purchase-config]').textContent);
        this.config.labels = { ...this.querySelector('[data-purchase-labels]').dataset };
      } catch (_) {
        return;
      }
      if (!this.form || !this.fieldset || !this.radios.length) return;
      this.initialized = true;
      this.choice = this.dataset.initialChoice;
      this.updateUrl = this.closest('product-info')?.dataset.updateUrl === 'true';
      if (this.updateUrl && new URL(window.location.href).searchParams.get('cista_size') === 'set') {
        const pair = this.radios.find(radio => radio.value === 'set');
        if (pair && !pair.disabled) {
          this.choice = 'set';
          pair.checked = true;
        }
      }
      this.fieldset.hidden = false;
      this.querySelector('[data-purchase-fallback]').hidden = true;
      this.buttonPrice.hidden = false;
      if (document.querySelector('cart-drawer')) {
        this.button.setAttribute('aria-haspopup', 'dialog');
        this.stickyButton?.setAttribute('aria-haspopup', 'dialog');
      }
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.fieldset.addEventListener('change', event => {
        if (event.target.matches('.cista-purchase__radio')) this.selectChoice(event.target.value);
      });
      this.updatePrices(null);
      this.mountSticky();
      this.queueStickyUpdate = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = 0;
          this.measureSticky();
        });
      };
      this.stickyButton?.addEventListener('click', () => {
        if (this.button.disabled) return;
        this.stickyRequested = true;
        this.form.requestSubmit(this.button);
      });
      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(this.queueStickyUpdate);
        this.footerObserver = new IntersectionObserver(this.queueStickyUpdate);
      }
      this.observeSticky();
      this.observePrices();
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
      this.footerObserver?.disconnect();
      window.removeEventListener('scroll', this.queueStickyUpdate);
      window.removeEventListener('resize', this.queueStickyUpdate);
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = 0;
      this.priceConnected = false;
      this.priceRequest?.abort();
      this.priceUnsubscribe?.();
      this.priceUnsubscribe = null;
      window.removeEventListener('pageshow', this.priceRefresh);
      window.removeEventListener('focus', this.priceRefresh);
      document.removeEventListener('visibilitychange', this.priceResume);
      document.removeEventListener('shopify:cart:discount-update', this.priceCartEvent);
      document.removeEventListener('shopify:cart:lines-update', this.priceCartEvent);
      this.sticky?.remove();
    }

    observePrices() {
      if (this.priceConnected) return;
      this.priceConnected = true;
      this.priceRefresh ||= () => this.refreshPrices();
      this.priceResume ||= () => {
        if (document.visibilityState === 'visible') this.refreshPrices();
      };
      this.priceCartEvent ||= event => {
        if (event.promise?.then) event.promise.then(this.priceRefresh, this.priceRefresh);
        else this.refreshPrices();
      };
      window.addEventListener('pageshow', this.priceRefresh);
      window.addEventListener('focus', this.priceRefresh);
      document.addEventListener('visibilitychange', this.priceResume);
      document.addEventListener('shopify:cart:discount-update', this.priceCartEvent);
      document.addEventListener('shopify:cart:lines-update', this.priceCartEvent);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.priceUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.priceRefresh);
      }
      this.refreshPrices();
    }

    async refreshPrices() {
      if (!this.priceConnected) return;
      this.priceRequest?.abort();
      const request = new AbortController();
      this.priceRequest = request;
      const timeout = setTimeout(() => request.abort(), 8000);
      try {
        // Read the buyer's real cart. Never add/remove bags just to preview prices.
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          credentials: 'same-origin', cache: 'no-store', signal: request.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Cart prices unavailable');
        const cart = await response.json();
        if (this.priceConnected && this.priceRequest === request) this.updatePrices(cart);
      } catch (_) {
        if (this.priceConnected && this.priceRequest === request) this.updatePrices(null);
      } finally {
        clearTimeout(timeout);
      }
    }

    formatPrice(amount) {
      // Use a Liquid-rendered sample so separators and currency placement match this market.
      const sample = this.config.moneySample || '';
      const match = sample.match(/\d[\d.,'’\u00a0\u202f ]*\d/);
      if (!match) return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.config.currency || 'EUR'
      }).format(amount / 100);
      const decimal = match[0].match(/([.,])89$/)?.[1];
      const integerSample = decimal ? match[0].slice(0, -3) : match[0];
      const separator = integerSample.replace(/\d/g, '')[0] || '';
      const parts = (amount / 100).toFixed(decimal ? 2 : 0).split('.');
      if (separator) parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      return sample.replace(match[0], parts[0] + (decimal ? decimal + parts[1] : ''));
    }

    updatePrices(cart) {
      this.quotes = cistaPurchaseQuotes(this.config, cart);
      this.radios.forEach(radio => {
        const quote = this.quotes[radio.value];
        if (!quote) return;
        const card = radio.nextElementSibling;
        radio.dataset.price = this.formatPrice(quote.amount);
        card.querySelector('[data-purchase-price]').textContent = radio.dataset.price;
        card.querySelector('[data-purchase-original]').textContent = radio.dataset.originalPrice;
        card.querySelector('[data-purchase-compare]').hidden = !quote.discounted;
      });
      this.updateSelection();
    }

    mountSticky() {
      if (!this.sticky) return;
      this.sticky.hidden = true;
      this.sticky.dataset.purchaseStickyOwner = this.form.id;
      document.querySelectorAll('[data-purchase-sticky-owner]').forEach(sticky => {
        if (sticky !== this.sticky && sticky.dataset.purchaseStickyOwner === this.form.id) sticky.remove();
      });
      document.body.append(this.sticky);
    }

    observeSticky() {
      this.purchasePassed = false;
      this.footerVisible = false;
      this.stickyObserver?.observe(this.button);
      const footer = document.querySelector('footer.footer');
      if (footer) this.footerObserver?.observe(footer);
      window.addEventListener('scroll', this.queueStickyUpdate, { passive: true });
      window.addEventListener('resize', this.queueStickyUpdate, { passive: true });
      this.updateStickyVisibility();
      this.queueStickyUpdate();
    }

    measureSticky() {
      const button = this.button.getBoundingClientRect();
      const footer = document.querySelector('footer.footer')?.getBoundingClientRect();
      this.purchasePassed = button.height > 0 && button.bottom <= 0;
      this.footerVisible = Boolean(footer && footer.top < window.innerHeight && footer.bottom > 0);
      this.updateStickyVisibility();
    }

    updateStickyVisibility() {
      if (this.sticky) this.sticky.hidden = !this.purchasePassed || this.footerVisible;
    }

    selectChoice(choice) {
      const radio = this.radios?.find(input => input.value === choice);
      if (!radio || radio.disabled || this.busy || this.uncertain) return false;
      this.choice = choice;
      radio.checked = true;
      this.showError();
      this.updateSelection();
      if (this.updateUrl) {
        const url = new URL(window.location.href);
        if (choice === 'set') {
          url.searchParams.delete('variant');
          url.searchParams.set('cista_size', 'set');
        } else {
          url.searchParams.set('variant', radio.dataset.variantId);
          url.searchParams.delete('cista_size');
        }
        window.history.replaceState(window.history.state, '', url);
      }
      this.dispatchEvent(new CustomEvent('cista:choice-change', {
        bubbles: true,
        detail: { choice, variantId: Number(radio.dataset.variantId) }
      }));
      return true;
    }

    updateSelection() {
      const radio = this.radios.find(input => input.value === this.choice);
      if (!radio) return;
      const quote = this.quotes?.[radio.value];
      const orderLabel = quote?.orderPercent ? this.config.labels.orderDiscount
        .replace('[code]', quote.orderCode).replace('[percent]', quote.orderPercent) : '';
      if (this.orderDiscount) {
        this.orderDiscount.hidden = !orderLabel;
        this.orderDiscount.textContent = orderLabel;
      }
      this.variantInput.value = radio.dataset.variantId;
      this.button.disabled = Boolean(radio.disabled || this.busy || this.uncertain);
      this.buttonText.textContent = this.busy ? this.config.labels.adding : radio.disabled ? this.config.labels.soldOut : this.config.labels.add;
      this.buttonPrice.textContent = ` · ${radio.dataset.price}`;
      this.buttonPrice.hidden = Boolean(this.busy || radio.disabled);
      this.spinner.hidden = !this.busy;
      this.form.setAttribute('aria-busy', String(Boolean(this.busy)));
      if (this.stickyButton) {
        this.stickyButton.disabled = this.button.disabled;
        this.stickyButton.textContent = this.buttonText.textContent;
        this.stickyChoice.textContent = radio.nextElementSibling.querySelector('.cista-purchase__title').textContent;
        this.stickyPrice.textContent = radio.dataset.price;
        if (this.stickyCompare) this.stickyCompare.hidden = !quote?.discounted;
        if (this.stickyOriginal) this.stickyOriginal.textContent = radio.dataset.originalPrice;
        if (this.stickyMedia && this.stickyMedia.dataset.choice !== radio.value) {
          const image = radio.nextElementSibling.querySelector('.cista-purchase__image');
          this.stickyMedia.replaceChildren(...(image ? [image.cloneNode(true)] : []));
          this.stickyMedia.dataset.choice = radio.value;
        }
      }
    }

    showError(message = '', showCartLink = false) {
      this.errorText.textContent = message;
      this.error.hidden = !message;
      this.cartLink.hidden = !showCartLink;
      if (message && this.submissionFromSticky) {
        this.error.setAttribute('tabindex', '-1');
        this.error.scrollIntoView?.({ block: 'center', behavior: 'auto' });
        this.error.focus({ preventScroll: true });
      }
    }

    getItems() {
      if (this.choice === 'set') {
        return [{ id: this.config.smallId, quantity: 1 }, { id: this.config.largeId, quantity: 1 }];
      }
      return [{ id: this.choice === 'large' ? this.config.largeId : this.config.smallId, quantity: 1 }];
    }

    startCartEvent(items) {
      const Event = window.StandardEvents?.CartLinesUpdateEvent;
      if (!Event) return null;
      const pending = Event.createPromise();
      pending.promise.catch(() => {});
      this.dispatchEvent(new Event({
        action: 'add', context: 'product',
        lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: pending.promise
      }));
      return pending;
    }

    finishCartEvent(pending) {
      const Event = window.StandardEvents?.CartLinesUpdateEvent;
      if (!pending || !Event) return;
      fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(response => {
          if (!response.ok) throw new Error('Cart response unavailable');
          return response.json();
        })
        .then(cart => {
          if (!cart?.currency) throw new Error('Cart currency unavailable');
          pending.resolve({ cart: Event.createCartFromAjaxResponse(cart) });
        })
        .catch(error => pending.reject(error));
    }

    publishCartError(message, variantId, code) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, { source: 'cista-purchase', productVariantId: variantId, errors: message, message });
      }
      const Event = window.StandardEvents?.CartErrorEvent;
      if (Event) this.dispatchEvent(new Event({ error: message, code }));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.uncertain || this.button.disabled) return;
      const selected = this.radios.find(input => input.value === this.choice);
      if (!selected || selected.disabled) return;
      this.submissionFromSticky = Boolean(this.stickyRequested || document.activeElement === this.stickyButton);
      this.stickyRequested = false;
      const items = this.getItems();
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map(section => section.id) || [];
      const payload = { items };
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }
      this.busy = true;
      this.fieldset.disabled = true;
      this.showError();
      this.updateSelection();
      let pending;
      try { pending = this.startCartEvent(items); } catch (_) {}
      let state;
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });
        state = await response.json();
        if (response.status >= 500) throw new Error('Cart update not confirmed');
        if (!response.ok || state.status) {
          const message = typeof state.description === 'string' ? state.description : typeof state.message === 'string' ? state.message : this.config.labels.error;
          this.showError(message);
          this.publishCartError(message, items[0].id, 'INVALID');
          pending?.reject(new Error(message));
          return;
        }
      } catch (error) {
        this.uncertain = true;
        this.showError(this.config.labels.uncertain, true);
        this.publishCartError(this.config.labels.uncertain, items[0].id, 'SERVICE_UNAVAILABLE');
        pending?.reject(error);
        return;
      } finally {
        this.busy = false;
        this.fieldset.disabled = false;
        this.updateSelection();
      }
      this.finishCartEvent(pending);
      this.refreshPrices();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          const update = publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: items[0].id, cartData: state });
          update?.catch?.(() => {});
        } catch (_) {}
      }
      if (!cart || !sections.every(id => typeof state.sections?.[id] === 'string')) {
        window.location.assign(this.dataset.cartUrl);
        return;
      }
      try {
        cart.classList.remove('is-empty');
        const firstItem = state.items?.[0] || state;
        cart.renderContents({ ...state, id: firstItem.id, key: firstItem.key });
      } catch (_) {
        window.location.assign(this.dataset.cartUrl);
      }
    }
  });
}
